import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const requestReset = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSent(true);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al solicitar recuperación");
        } finally {
            setLoading(false);
        }
    };

    const confirmReset = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            toast.success("Contraseña actualizada. Ya puedes iniciar sesión.");
            navigate("/login");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Token inválido o expirado");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-8">
            <div className="w-full max-w-sm space-y-6" data-testid="reset-password-page">
                <div className="flex items-center gap-3">
                    <Logo variant="seal" size={48} />
                    <span className="font-display text-2xl">Hacienda Nápoles</span>
                </div>

                {token ? (
                    <form onSubmit={confirmReset} className="space-y-5" data-testid="reset-confirm-form">
                        <h1 className="font-display text-3xl">Nueva contraseña</h1>
                        <div className="flex flex-col gap-2">
                            <Input
                                data-testid="input-new-password"
                                type="password"
                                required
                                minLength={6}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground h-12"
                            />
                            <Label className="overline text-muted-foreground">Nueva contraseña (mín. 6 caracteres)</Label>
                        </div>
                        <Button data-testid="btn-confirm-reset" disabled={loading} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/85 h-11">
                            {loading ? "Guardando…" : "Cambiar contraseña"}
                        </Button>
                    </form>
                ) : sent ? (
                    <div data-testid="reset-sent">
                        <h1 className="font-display text-3xl">Revisa tu correo</h1>
                        <p className="text-sm text-muted-foreground mt-3">
                            Si el correo existe en nuestro sistema, te enviamos un enlace de recuperación válido durante 1 hora.
                        </p>
                    </div>
                ) : (
                    <form onSubmit={requestReset} className="space-y-5" data-testid="reset-request-form">
                        <h1 className="font-display text-3xl">Recuperar contraseña</h1>
                        <p className="text-sm text-muted-foreground">Te enviaremos un enlace de recuperación con expiración de 1 hora.</p>
                        <div className="flex flex-col gap-2">
                            <Input
                                data-testid="input-reset-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="tu@email.com"
                                className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground h-12"
                            />
                            <Label className="overline text-muted-foreground">Email</Label>
                        </div>
                        <Button data-testid="btn-request-reset" disabled={loading} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/85 h-11">
                            {loading ? "Enviando…" : "Enviar enlace de recuperación"}
                        </Button>
                    </form>
                )}

                <div className="text-xs text-muted-foreground">
                    <Link to="/login" className="underline-editorial" data-testid="link-back-login">← Volver a iniciar sesión</Link>
                </div>
            </div>
        </div>
    );
}
